/**
 * email-rule — ONE rule for a deliverable e-mail address.
 *
 * TWIN of main-backend `org.nikita.utils.EmailRule` and of the same file in
 * main-consumer (byte-identical copies, no imports). The truth is the fixture
 * `email-rule.json` (source: main-backend/src/test/resources/email-rule.json,
 * copies pinned by e2e_main/scripts/check-fixture-parity.sh); every twin
 * replays every case of it and pins its pattern and tables against it.
 *
 * Why it exists (2026-10-05): a guest address pasted into the cockpit carried
 * U+200E in front and U+200B after the "@". Every write door accepted it
 * (`^[^@\s]+@[^@\s]+\.[^@\s]+$` knows neither), the consumer handed it to SES
 * raw, SES answered `400 Local address contains control or whitespace`, and
 * the tenant saw "failed" with no reason. SES does not speak SMTPUTF8: before
 * the "@" only ASCII is deliverable, and the domain must be LDH (a typed
 * `xn--` form passes; an umlaut domain is refused in v1 — no Punycode yet).
 *
 * Division of labour (plan "Saubere Eingaben"):
 *   - clients heal: `cleanEmail` strips what no address needs (format and
 *     control characters everywhere, whitespace at the edges) BEFORE the value
 *     is sent; the visible text never changes;
 *   - servers judge: `isDeliverableEmail` on the normalised value, refuse with
 *     `emailRuleReason`; nothing is rewritten silently;
 *   - the consumer skips: `isDeliverableEmail(guest.email)` before SES, never
 *     a rewrite of the recipient.
 *
 * The pattern is compiled WITHOUT flags. Never add `m` (a trailing newline
 * would match `^`), never `u`/`v` (`v` does not even compile it), never hand
 * it to an HTML `pattern=` attribute, and in Mongo use it as `$regex` with no
 * `$options`. Never write it as a template literal (it contains a backtick
 * nowhere today, but the twin rule is "one string, one truth").
 */

/** Must equal `patternSource` in email-rule.json. */
export const EMAIL_RULE_PATTERN_SOURCE =
  "^(?=[!-~]{6,254}(?![\\s\\S]))(?=[^@]{1,64}@)[A-Za-z0-9!#$%&'*+/=?^_~-]+(?:\\.[A-Za-z0-9!#$%&'*+/=?^_~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\\.)+[A-Za-z](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])(?![\\s\\S])";

export const EMAIL_RULE_MAX_LENGTH = 254;
export const EMAIL_RULE_MAX_LOCAL_LENGTH = 64;

/**
 * Code points an address never needs: C0/C1 controls that are not whitespace,
 * DEL, soft hyphen, combining grapheme joiner, the bidi marks and isolates,
 * zero-width characters, word joiner, variation selectors, BOM, interlinear
 * annotation, tag characters. Inclusive ranges. Must equal `invisibleRanges`
 * in email-rule.json.
 */
export const EMAIL_RULE_INVISIBLE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x0008],
  [0x000e, 0x001f],
  [0x007f, 0x0084],
  [0x0086, 0x009f],
  [0x00ad, 0x00ad],
  [0x034f, 0x034f],
  [0x061c, 0x061c],
  [0x180e, 0x180e],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x2066, 0x206f],
  [0xfe00, 0xfe0f],
  [0xfeff, 0xfeff],
  [0xfff9, 0xfffb],
  [0xe0000, 0xe007f],
  [0xe0100, 0xe01ef],
];

/**
 * Whitespace: removed at the EDGES only. Inside an address it is a reason to
 * refuse ("max mustermann@…" is not an address, and joining the halves would
 * be a guess). Must equal `whitespaceRanges` in email-rule.json. Same set as
 * the edge normaliser of every repo (`FieldValueNormalizer`), plus NEL.
 */
export const EMAIL_RULE_WHITESPACE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0009, 0x000d],
  [0x0020, 0x0020],
  [0x0085, 0x0085],
  [0x00a0, 0x00a0],
  [0x1680, 0x1680],
  [0x2000, 0x200a],
  [0x2028, 0x2029],
  [0x202f, 0x202f],
  [0x205f, 0x205f],
  [0x3000, 0x3000],
];

/**
 * Why a value is not a deliverable address, in the order the reasons are
 * tried. The first that applies wins:
 *   invisible        a code point from EMAIL_RULE_INVISIBLE_RANGES anywhere
 *   whitespace       a code point from EMAIL_RULE_WHITESPACE_RANGES anywhere
 *   shape            not exactly one "@" (also: empty)
 *   local-non-ascii  a non-ASCII code point before the "@"
 *   local-too-long   more than 64 characters before the "@"
 *   domain           the part after the "@" is not an LDH hostname with a
 *                    letter-led TLD of two or more characters (also: non-ASCII)
 *   too-long         more than 254 characters in total
 *   shape            anything else the pattern refuses (dots at the edges of
 *                    the local part, "..", characters outside the local set)
 */
export type EmailRuleReason =
  | "invisible"
  | "whitespace"
  | "shape"
  | "local-non-ascii"
  | "local-too-long"
  | "domain"
  | "too-long";

export const EMAIL_RULE_REASONS: readonly EmailRuleReason[] = [
  "invisible",
  "whitespace",
  "shape",
  "local-non-ascii",
  "local-too-long",
  "domain",
  "too-long",
];

const PATTERN = new RegExp(EMAIL_RULE_PATTERN_SOURCE);
const DOMAIN = new RegExp(
  "^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\\.)+[A-Za-z](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])$",
);

function inRanges(cp: number, ranges: ReadonlyArray<readonly [number, number]>): boolean {
  for (const [lo, hi] of ranges) {
    if (cp >= lo && cp <= hi) return true;
  }
  return false;
}

const isInvisible = (cp: number) => inRanges(cp, EMAIL_RULE_INVISIBLE_RANGES);
const isWhitespace = (cp: number) => inRanges(cp, EMAIL_RULE_WHITESPACE_RANGES);

/** The value as stored passes the one rule. No trimming, no cleaning here. */
export function isDeliverableEmail(value: string): boolean {
  return PATTERN.test(value);
}

/**
 * What a CLIENT does with what the user typed or pasted, on every change:
 * drop every invisible code point anywhere, then drop whitespace at the
 * edges. Nothing else — inner whitespace and non-ASCII letters stay and are
 * refused by `isDeliverableEmail`, with a reason the user can act on.
 * Idempotent; the identity on every deliverable address.
 */
export function cleanEmail(raw: string): string {
  const kept: string[] = [];
  for (const ch of raw) {
    if (!isInvisible(ch.codePointAt(0) as number)) kept.push(ch);
  }
  // `-1` for a missing index is in no range, so the loops stop at the ends
  // (and the file compiles with `noUncheckedIndexedAccess` on or off).
  const cpOf = (ch: string | undefined): number => (ch === undefined ? -1 : (ch.codePointAt(0) as number));
  let start = 0;
  let end = kept.length;
  while (start < end && isWhitespace(cpOf(kept[start]))) start++;
  while (end > start && isWhitespace(cpOf(kept[end - 1]))) end--;
  return kept.slice(start, end).join("");
}

function hasAny(value: string, test: (cp: number) => boolean): boolean {
  for (const ch of value) {
    if (test(ch.codePointAt(0) as number)) return true;
  }
  return false;
}

const isAsciiVisible = (cp: number) => cp >= 0x21 && cp <= 0x7e;

/**
 * `null` when the value is a deliverable address, else the first reason in
 * the documented order. A server computes this on the NORMALISED value it
 * would store; a client on the cleaned value it would send.
 */
export function emailRuleReason(value: string): EmailRuleReason | null {
  if (PATTERN.test(value)) return null;
  if (hasAny(value, isInvisible)) return "invisible";
  if (hasAny(value, isWhitespace)) return "whitespace";
  const at = value.indexOf("@");
  if (at < 0 || value.indexOf("@", at + 1) !== -1) return "shape";
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (hasAny(local, (cp) => !isAsciiVisible(cp))) return "local-non-ascii";
  if (local.length > EMAIL_RULE_MAX_LOCAL_LENGTH) return "local-too-long";
  if (hasAny(domain, (cp) => !isAsciiVisible(cp)) || !DOMAIN.test(domain)) return "domain";
  if (value.length > EMAIL_RULE_MAX_LENGTH) return "too-long";
  return "shape";
}

/**
 * The character a message can name for `invisible`, `whitespace`,
 * `local-non-ascii` and a non-ASCII `domain`: the first offending code point
 * as the character itself and as `U+XXXX`. `null` for every other reason.
 */
export function emailRuleOffender(
  value: string,
  reason: EmailRuleReason,
): { char: string; codePoint: string } | null {
  let test: ((cp: number) => boolean) | null = null;
  let haystack = value;
  if (reason === "invisible") test = isInvisible;
  else if (reason === "whitespace") test = isWhitespace;
  else if (reason === "local-non-ascii" || reason === "domain") {
    const at = value.indexOf("@");
    haystack = reason === "local-non-ascii" ? value.slice(0, at < 0 ? value.length : at) : value.slice(at + 1);
    test = (cp) => !isAsciiVisible(cp);
  }
  if (test === null) return null;
  for (const ch of haystack) {
    const cp = ch.codePointAt(0) as number;
    if (test(cp)) {
      return { char: ch, codePoint: "U+" + cp.toString(16).toUpperCase().padStart(4, "0") };
    }
  }
  return null;
}
